import React from 'react';
import './auth.css';

/** Кружок ожидания в кнопке. Один на все формы входа и регистрации. */
export function Spinner() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" className="qp-auth-spinner" aria-hidden>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" opacity="0.25" fill="none" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" />
    </svg>
  );
}

interface NoticeProps {
  children: React.ReactNode;
  /** `info` — человек ошибся дверью и его перенесли; `error` — что-то сломалось. */
  tone?: 'info' | 'error';
}

/**
 * Плашка над формой.
 *
 * Тон по умолчанию информационный: чаще всего сюда попадает объяснение
 * переноса между входом и регистрацией, а человек в этом случае ничего не
 * сделал не так.
 */
export function Notice({ children, tone = 'info' }: NoticeProps) {
  return (
    <div className={`qp-auth-notice qp-auth-notice--${tone}`} role="status">
      {children}
    </div>
  );
}

interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  hint?: string;
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;
  inputMode?: 'text' | 'numeric' | 'tel';
  autoComplete?: string;
  /** Обязательное поле — звёздочка у подписи (строка 49 QA-таблицы). */
  required?: boolean;
}

/** Звёздочка обязательного поля — та же, что у полей кабинета (`Field`). */
function RequiredMark() {
  return (
    <span className="qp-field-req" aria-hidden>
      {' '}
      *
    </span>
  );
}

/** Текстовое поле в стиле кабинета: подпись плавает, ошибка живёт под полем. */
export function TextField({
  id,
  label,
  value,
  onChange,
  error,
  hint,
  placeholder = ' ',
  disabled,
  maxLength,
  inputMode,
  autoComplete,
  required,
}: TextFieldProps) {
  return (
    <div className="qp-auth-control">
      <label className={`m3-field${error ? ' m3-field--error' : ''}`} htmlFor={id}>
        <input
          id={id}
          name={id}
          type="text"
          className="m3-field-input"
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          maxLength={maxLength}
          inputMode={inputMode}
          autoComplete={autoComplete}
          aria-invalid={error ? true : undefined}
          aria-required={required || undefined}
        />
        <span className="m3-field-label">
          {label}
          {required && <RequiredMark />}
        </span>
      </label>
      {error ? (
        <span className="m3-field-support m3-field-support--error" role="alert">
          {error}
        </span>
      ) : (
        hint && <span className="m3-field-support">{hint}</span>
      )}
    </div>
  );
}

interface SelectOption {
  /** Города и категории приходят числовыми id — в разметке они всё равно строки. */
  value: string | number;
  label: string;
}

/** Раздел списка — `<optgroup>`: категории по разделам «Поесть», «Красота»… */
interface SelectOptionGroup {
  label: string;
  options: SelectOption[];
}

/** Пункт списка или раздел с пунктами. */
export type SelectItem = SelectOption | SelectOptionGroup;

interface SelectFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Пункты и разделы — в том порядке, в каком их показать. */
  options: SelectItem[];
  placeholder: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
}

/** Пункт списка — и вне раздела, и внутри `<optgroup>`. */
function renderOption(option: SelectOption) {
  return (
    <option key={option.value} value={String(option.value)}>
      {option.label}
    </option>
  );
}

/**
 * Список выбора.
 *
 * Родной `<select>`, а не самодельный дропдаун: города и категории приходят
 * с сервера длинными списками, и встроенный поиск браузера по первым буквам
 * тут полезнее любой стилизации. Длинный список режется на разделы родным же
 * `<optgroup>`.
 */
export function SelectField({
  id,
  label,
  value,
  onChange,
  options,
  placeholder,
  error,
  disabled,
  required,
}: SelectFieldProps) {
  return (
    <div className="qp-auth-control">
      <label className="qp-auth-select-label" htmlFor={id}>
        {label}
        {required && <RequiredMark />}
      </label>
      <select
        id={id}
        name={id}
        className={`qp-auth-select${error ? ' qp-auth-select--error' : ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
      >
        <option value="">{placeholder}</option>
        {options.map((item, index) =>
          'options' in item ? (
            <optgroup key={`group-${index}`} label={item.label}>
              {item.options.map(renderOption)}
            </optgroup>
          ) : (
            renderOption(item)
          ),
        )}
      </select>
      {error && (
        <span className="m3-field-support m3-field-support--error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
